import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  listQlikConnections,
  listOrganizationMemberships,
  createQlikConnection,
  getQlikConnection,
  deleteQlikConnection,
  testQlikConnection,
  updateQlikConnection,
  listAppsFromConnection,
  getAppFromConnection,
  listVariablesFromApp,
  updateVariableInApp,
  listDimensionsFromApp,
  updateDimensionInApp,
  listFieldsFromApp,
  listMeasuresFromApp,
  updateMeasureInApp,
  getAppMetadataCache,
  refreshAppMetadata,
  refreshAllAppsMetadata,
  type QlikVariable,
  type QlikDimension,
  type QlikField,
  type QlikMeasure,
  type OrganizationMembership,
  type QlikAppMetadataCache,
} from "@/lib/api/qlik-connections";
import type {
  QlikConnection,
  CreateQlikConnectionInput,
  UpdateQlikConnectionInput,
  UpdateQlikDimensionInput,
  UpdateQlikMeasureInput,
  QlikApp,
} from "@repo/shared";
import { groupKeys } from "@/features/groups/api";

const qlikAppContentQueryConfig = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
} as const;

// Query keys factory for type-safe cache management
export const connectionKeys = {
  all: ["qlik-connections"] as const,
  organizations: () => [...connectionKeys.all, "organizations"] as const,
  lists: () => [...connectionKeys.all, "list"] as const,
  list: (filters?: { search?: string }) => [...connectionKeys.lists(), filters] as const,
  details: () => [...connectionKeys.all, "detail"] as const,
  detail: (id: string) => [...connectionKeys.details(), id] as const,
  apps: (connectionId: string) => [...connectionKeys.detail(connectionId), "apps"] as const,
  app: (connectionId: string, appId: string) =>
    [...connectionKeys.apps(connectionId), "app", appId] as const,
  variables: (connectionId: string, appId: string) =>
    [...connectionKeys.apps(connectionId), appId, "variables"] as const,
  dimensions: (connectionId: string, appId: string) =>
    [...connectionKeys.apps(connectionId), appId, "dimensions"] as const,
  measures: (connectionId: string, appId: string) =>
    [...connectionKeys.apps(connectionId), appId, "measures"] as const,
  fields: (connectionId: string, appId: string) =>
    [...connectionKeys.apps(connectionId), appId, "fields"] as const,
  metadata: (connectionId: string, appId: string) =>
    [...connectionKeys.apps(connectionId), appId, "metadata"] as const,
};

export async function discardAppQueries(
  queryClient: QueryClient,
  connectionId: string,
  appId: string,
) {
  queryClient.removeQueries({ queryKey: connectionKeys.variables(connectionId, appId) });
  queryClient.removeQueries({ queryKey: connectionKeys.dimensions(connectionId, appId) });
  queryClient.removeQueries({ queryKey: connectionKeys.measures(connectionId, appId) });
  queryClient.removeQueries({ queryKey: connectionKeys.fields(connectionId, appId) });
  queryClient.removeQueries({ queryKey: connectionKeys.metadata(connectionId, appId) });

  await queryClient.invalidateQueries({
    queryKey: connectionKeys.apps(connectionId),
  });
}

export const organizationMembershipsQueryOptions = queryOptions({
  queryKey: connectionKeys.organizations(),
  queryFn: listOrganizationMemberships,
});

// Query options for fetching all connections
export const connectionsQueryOptions = queryOptions({
  queryKey: connectionKeys.lists(),
  queryFn: listQlikConnections,
});

// Query options for fetching a single connection
export const connectionQueryOptions = (id: string) =>
  queryOptions({
    queryKey: connectionKeys.detail(id),
    queryFn: () => getQlikConnection(id),
    enabled: !!id,
  });

// Query options for fetching apps from a connection
export const connectionAppsQueryOptions = (connectionId: string) =>
  queryOptions({
    queryKey: connectionKeys.apps(connectionId),
    queryFn: () => listAppsFromConnection(connectionId),
    enabled: !!connectionId,
    staleTime: 60_000, // 1 minute
  });

// Query options for fetching a single app from a connection. Lightweight (one
// Items call) — used by the single-app editor to resolve just the current app so
// a page reload no longer lists the whole tenant. Seeded from the apps-list cache
// when available (see the editor route) to stay free on list -> editor navigation.
export const connectionAppQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: connectionKeys.app(connectionId, appId),
    queryFn: () => getAppFromConnection(connectionId, appId),
    enabled: !!connectionId && !!appId,
    staleTime: 60_000,
  });

// Query options for fetching variables from an app
export const appVariablesQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: connectionKeys.variables(connectionId, appId),
    queryFn: () => listVariablesFromApp(connectionId, appId),
    enabled: !!connectionId && !!appId,
    ...qlikAppContentQueryConfig,
  });

// Mutation hook for creating a connection
export function useCreateConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateQlikConnectionInput) => createQlikConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
  });
}

// Mutation hook for testing a connection (health check)
export function useTestConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => testQlikConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
  });
}

// Mutation hook for deleting a connection
export function useDeleteConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteQlikConnection(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
      queryClient.removeQueries({ queryKey: connectionKeys.detail(id) });
    },
  });
}

// Mutation hook for updating a connection
export function useUpdateConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQlikConnectionInput }) =>
      updateQlikConnection(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(variables.id) });
    },
  });
}

// Mutation hook for updating a variable in an app
export function useUpdateVariable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      appId,
      variableId,
      data,
    }: {
      connectionId: string;
      appId: string;
      variableId: string;
      data: { name?: string; definition?: string; comment?: string };
    }) => updateVariableInApp(connectionId, appId, variableId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.variables(variables.connectionId, variables.appId),
      });
    },
  });
}

export type { QlikConnection, QlikApp, QlikVariable, OrganizationMembership };

// Query options for fetching dimensions from an app
export const appDimensionsQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: connectionKeys.dimensions(connectionId, appId),
    queryFn: () => listDimensionsFromApp(connectionId, appId),
    enabled: !!connectionId && !!appId,
    ...qlikAppContentQueryConfig,
  });

// Query options for fetching fields from an app's data model
export const appFieldsQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: connectionKeys.fields(connectionId, appId),
    queryFn: () => listFieldsFromApp(connectionId, appId),
    enabled: !!connectionId && !!appId,
    ...qlikAppContentQueryConfig,
  });

// Query options for fetching measures from an app
export const appMeasuresQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: connectionKeys.measures(connectionId, appId),
    queryFn: () => listMeasuresFromApp(connectionId, appId),
    enabled: !!connectionId && !!appId,
    ...qlikAppContentQueryConfig,
  });

// Mutation hook for updating a dimension in an app
export function useUpdateDimension() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      appId,
      dimensionId,
      data,
    }: {
      connectionId: string;
      appId: string;
      dimensionId: string;
      data: UpdateQlikDimensionInput;
    }) => updateDimensionInApp(connectionId, appId, dimensionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.dimensions(variables.connectionId, variables.appId),
      });
    },
  });
}

// Mutation hook for updating a measure in an app
export function useUpdateMeasure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      appId,
      measureId,
      data,
    }: {
      connectionId: string;
      appId: string;
      measureId: string;
      data: UpdateQlikMeasureInput;
    }) => updateMeasureInApp(connectionId, appId, measureId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.measures(variables.connectionId, variables.appId),
      });
    },
  });
}

// Query options for fetching cached metadata for a specific app.
// The GET endpoint returns `data: null` when no cache exists yet (normal first-load state,
// not an error); in that case we populate the cache lazily via the refresh endpoint.
export const appMetadataQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: connectionKeys.metadata(connectionId, appId),
    queryFn: async () => {
      const cache = await getAppMetadataCache(connectionId, appId);
      if (cache) return cache;
      return refreshAppMetadata(connectionId, appId);
    },
    enabled: !!connectionId && !!appId,
    ...qlikAppContentQueryConfig,
  });

// Mutation hook for refreshing metadata cache for a single app
export function useRefreshAppMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectionId, appId }: { connectionId: string; appId: string }) =>
      refreshAppMetadata(connectionId, appId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.metadata(variables.connectionId, variables.appId),
      });
      queryClient.invalidateQueries({
        queryKey: groupKeys.all,
      });
    },
  });
}

// Mutation hook for refreshing metadata cache for all apps in a connection
export function useRefreshAllAppsMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => refreshAllAppsMetadata(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.apps(connectionId),
      });
      queryClient.invalidateQueries({
        queryKey: groupKeys.all,
      });
    },
  });
}

export type { QlikDimension, QlikField, QlikMeasure, QlikAppMetadataCache };
